/**
 * Open a story at its end.
 *
 * `useStickToBottom` pins a feed that is *growing* — it is about a turn in progress and it
 * deliberately does nothing at rest. That left the cold load answering the wrong question: a
 * player returning to a forty-chapter story landed at chapter one, thousands of pixels above
 * the paragraph they left off on, with no control that says "take me to the end" and a scroll
 * bar that is the only clue there is anything down there. A story is a log, the newest entry
 * is the one being read, and every reader of one arrives at the bottom of it.
 *
 * **It is a jump and not a scroll**, on purpose and in both senses: `behavior: 'auto'` so
 * nothing animates, and it runs in a layout effect so it happens *before the first paint* of
 * the feed. An animated version would show the reader forty chapters they did not ask to see
 * and would land on a moving target while the page is still laying out; a passive effect would
 * paint chapter one first and then snap, which reads as a bug the first time and as a slow app
 * every time after.
 *
 * The second pass on the next frame is the one non-obvious line. The first jump measures a
 * document that the browser may not have finished with — a webfont swapping in reflows every
 * paragraph in the feed and moves the bottom by hundreds of pixels — so the jump is repeated
 * once the frame has settled, and only if the reader has not scrolled in the meantime. Beyond
 * that one frame the page belongs to them.
 *
 * jsdom has no layout: `scrollHeight` is 0, so this scrolls to 0 and asserts nothing.
 * `tests/e2e/feed.spec.ts` is where it is held, alongside the two hooks it has to not fight.
 */
import { useLayoutEffect } from 'react';

/**
 * @param ready whether there is a story in the document to be at the end of. The story screen's
 * first commit renders *"Loading the story…"*, and landing at the end of that is landing
 * nowhere — the flag is what makes this run against the feed rather than against the notice
 * that precedes it.
 */
export function useOpenAtEnd(ready: boolean): void {
  useLayoutEffect(() => {
    if (!ready) return;

    const toEnd = (): number => {
      window.scrollTo({ top: document.documentElement.scrollHeight, behavior: 'auto' });
      return window.scrollY;
    };

    const landed = toEnd();

    // jsdom without `pretendToBeVisual` has no rAF, and a browser mid-teardown may not run
    // the callback. Either way the jump above already happened; this only refines it.
    if (typeof requestAnimationFrame !== 'function') return;

    const frame = requestAnimationFrame(() => {
      // A reader who has already scrolled owns the page. `landed` is what *we* left it at, so
      // any difference is theirs — including a wheel notch during the first frame.
      if (Math.abs(window.scrollY - landed) > 1) return;
      toEnd();
    });

    return () => {
      cancelAnimationFrame(frame);
    };
  }, [ready]);
}
